import { apiFetch } from '../auth/apiClient'

export interface ScheduleJob {
  job_id: string
  title: string
  prompt: string
  frequency: 'once' | 'daily' | 'weekly' | 'monthly'
  time_of_day: string
  weekday: number | null
  day_of_month: number | null
  once_on: string | null
  schedule_summary: string
  next_run_at: string | null
  next_run_at_local: string | null
  status: 'active' | 'cancelled' | 'completed'
  seen_at: string | null
  user_email: string
  created_at: string | null
}

export interface ScheduleRun {
  id: string
  scheduled_for: string | null
  started_at: string | null
  completed_at: string | null
  status: 'pending' | 'running' | 'sent' | 'failed'
  error: string | null
  email_subject: string | null
}

export interface SchedulesListPayload {
  jobs: ScheduleJob[]
}

export interface ScheduleDetailPayload {
  job: ScheduleJob
  runs: ScheduleRun[]
}

export interface SendNowPayload {
  status: 'queued'
  to: string
  note: string
}

export interface ScheduleRunChatPromptPayload {
  prompt: string
}

export interface UpdateSchedulePayload {
  title: string
  prompt: string
  frequency: ScheduleJob['frequency']
  time_of_day: string // 'HH:MM'
  weekday?: number | null
  day_of_month?: number | null
  once_on?: string | null // 'YYYY-MM-DD'
}

function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  const next = encodeURIComponent(window.location.pathname + window.location.search)
  window.location.replace(`/login?next=${next}`)
}

async function handleErrorResponse(response: Response): Promise<never> {
  if (response.status === 401) redirectToLogin()
  let message = `Request failed with status ${response.status}`
  try {
    const payload = (await response.json()) as { error?: string; detail?: string }
    if (payload?.error) message = payload.error
    else if (payload?.detail) message = payload.detail
  } catch {
    // ignore
  }
  throw new Error(message)
}

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await apiFetch(url, {
    headers: { Accept: 'application/json' },
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

async function postJson<T>(
  url: string,
  signal?: AbortSignal,
  body?: Record<string, unknown>,
): Promise<T> {
  const response = await apiFetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: body == null ? undefined : JSON.stringify(body),
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

async function putJson<T>(
  url: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T> {
  const response = await apiFetch(url, {
    method: 'PUT',
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  })
  if (!response.ok) await handleErrorResponse(response)
  return (await response.json()) as T
}

export function fetchSchedules(
  signal?: AbortSignal,
  includeInactive = false,
): Promise<SchedulesListPayload> {
  const query = includeInactive ? '?include_inactive=true' : ''
  return fetchJson<SchedulesListPayload>(`/api/schedules${query}`, signal)
}

export function fetchScheduleDetail(
  jobId: string,
  signal?: AbortSignal,
): Promise<ScheduleDetailPayload> {
  return fetchJson<ScheduleDetailPayload>(`/api/schedules/${encodeURIComponent(jobId)}`, signal)
}

export function fetchScheduleRunChatPrompt(
  runId: string,
  signal?: AbortSignal,
): Promise<ScheduleRunChatPromptPayload> {
  return fetchJson<ScheduleRunChatPromptPayload>(
    `/api/schedules/runs/${encodeURIComponent(runId)}/chat-prompt`,
    signal,
  )
}

export function cancelSchedule(jobId: string): Promise<{ ok: boolean; job_id: string }> {
  return postJson(`/api/schedules/${encodeURIComponent(jobId)}/cancel`)
}

export function sendScheduleNow(jobId: string): Promise<SendNowPayload> {
  return postJson<SendNowPayload>(`/api/schedules/${encodeURIComponent(jobId)}/send_now`)
}

export function markSchedulesSeen(jobIds: string[]): Promise<{ ok: boolean; marked: number }> {
  return postJson<{ ok: boolean; marked: number }>('/api/schedules/seen', undefined, {
    job_ids: jobIds,
  }).then((result) => {
    window.dispatchEvent(new CustomEvent('credem:schedules-updated'))
    return result
  })
}

export function updateSchedule(
  jobId: string,
  payload: UpdateSchedulePayload,
): Promise<{ job: ScheduleJob }> {
  return putJson<{ job: ScheduleJob }>(
    `/api/schedules/${encodeURIComponent(jobId)}`,
    payload as unknown as Record<string, unknown>,
  )
}
